<template>
  <!-- 登录页面外层容器：分为背景区+登录区 -->
  <div class="login-page">
    <!-- 背景图区域：占3/4宽度 -->
    <div class="login-bg-section"></div>

    <!-- 登录表单区域：占1/4宽度 -->
    <div class="login-form-section" v-loading="loginLoading" :element-loading-text="loginLoadingTxt">
      <h1 class="main-title">收银数据集成系统</h1>
      <div class="login-form-wrapper">

        <h2 class="login-title">用户登录</h2>
        <el-form
          ref="loginFormRef"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          size="large"
        >
          <!-- 用户名输入框 -->
          <el-form-item prop="username">
            <el-input
              v-model="loginForm.username"
              placeholder="请输入用户名"
              :prefix-icon="User"
              type="text"
            />
          </el-form-item>

          <!-- 密码输入框 -->
          <el-form-item prop="password">
            <el-input
              v-model="loginForm.password"
              placeholder="请输入密码"
              :prefix-icon="Lock"
              show-password
            />
          </el-form-item>

          <!-- 操作按钮组 -->
          <el-form-item class="btn-group">
            <el-button
              type="primary"
              size="large"
              class="login-btn"
              @click="handleLogin"
              block
              :disabled="loginLoading"
            >
              登录
            </el-button>
          </el-form-item>
        </el-form>
      </div>
      <div class="copyright">
        <div>© 2026 收银数据集成系统 版权所有 | All Rights Reserved</div>
        <div>© 数据安全为本，严守行业合规规范，合规保障</div>
        <div>Developer: Xiong &nbsp;&nbsp;&nbsp;Version: {{version}}</div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {FormInstance, FormRules} from 'element-plus';
import { User, Lock } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router';
import {authApi } from "@/api";
import {handleApiError} from "@/utils/error";
const router = useRouter()
import {UserInfo, useUserStore} from "@/stores/user";
const userStore = useUserStore();

// 页面挂载时从本地存储读取用户名回填
onMounted(() => {
  const savedUsername = localStorage.getItem('loginUsername');
  if (savedUsername) {
    loginForm.value.username = savedUsername;
  }
});

// 表单实例
const loginFormRef = ref<FormInstance>();
const version = ref('1.13.24')

const loginLoading = ref(false)
const loginLoadingTxt = ref('')

// 表单数据
const loginForm = ref({
  username: '', // 手机号码
  password: '', // 密码
  terminal: 'PC'
});

// 表单校验规则
const loginRules = ref<FormRules>({
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
  ]
});

// 登录方法
const handleLogin = async () => {
  //await router.push('/index/home');
  userStore.clearToken();
  if (!loginFormRef.value) return;
  try {
    await loginFormRef.value.validate();
    loginLoading.value = true;
    loginLoadingTxt.value = '正在登录...';
    const response:UserInfo = await authApi.loginAuth(loginForm.value);
    userStore.setToken(response.token);
    userStore.setUserInfo(response);
    localStorage.setItem('loginUsername', loginForm.value.username);
    await router.push('/index/home');
  } catch (error) {
    handleApiError(error);
  }finally {
    loginLoading.value = false
    loginLoadingTxt.value = ''
  }
};

</script>

<style lang="scss" scoped>
// 全局页面容器：分为背景区(3/4) + 登录区(1/4)
.login-page {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: row;
  overflow: hidden;

  // 背景图区域：占3/4宽度
  .login-bg-section {
    width: 75%; // 3/4 宽度
    height: 100%;
    background: url('../assets/login/background-image.png') no-repeat center center / cover;
    flex-shrink: 0; // 防止被挤压
  }

  // 登录区：占1/4宽度
  .login-form-section {
    width: 25%; // 1/4 宽度
    height: 100%;
    // 核心修改：把横向布局改成垂直布局
    display: flex;
    flex-direction: column; // 新增这一行，让子元素垂直排列
    align-items: center;
    gap: 25px; // 新增：标题、表单、版权之间的垂直间距
    //background-color: #f8f9fa;
    //background-image: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    background-color: #f8f9fa;
    background-image: linear-gradient(135deg, #ffffff 0%, #d6eafd 100%);
    background-size: auto, cover;
    padding: 0 20px;
    box-sizing: border-box;

    .main-title {
      text-align: center;
      font-size: 30px;
      font-weight: 700;
      color: #192a48;
      margin-top: 150px;
      line-height: 1;
      width: 100%;
      // 新增底部装饰线
      position: relative;
      &::after {
        content: "";
        width: 60px;
        height: 4px;
        background: linear-gradient(90deg, #409eff, #67c23a);
        position: absolute;
        bottom: -12px;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 2px;
      }
    }

    // ✅ 新增：外部版权信息样式
    .copyright {
      text-align: center;
      font-size: 12px;
      color: #909399;
      line-height: 1.5;
      margin-top: 330px;
      width: 100%;

      div{
        margin-top: 5px;
      }
    }

    // 表单容器
    .login-form-wrapper {
      width: 100%;
      max-width: 380px;
      // 背景改为渐变
      background: linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.95) 100%);
      padding: 40px 30px;
      border-radius: 16px;
      // 内外双层阴影
      box-shadow:
          0 8px 24px rgba(0, 0, 0, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 0.8);
      box-sizing: border-box;
      margin-top: 150px;
      // 增加轻微悬浮感
      transform: translateY(-4px);
      transition: all 0.3s ease;
      &:hover {
        transform: translateY(-6px);
        box-shadow:
            0 12px 32px rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
      }

      // 登录标题
      .login-title {
        text-align: center;
        font-size: 24px;
        font-weight: 600;
        color: #303133;
        margin: 0 0 30px 0;
      }

      // 登录表单
      .login-form {
        width: 100%;

        // 表单项间距
        :deep(.el-form-item) {
          margin-bottom: 20px;
        }

        // 按钮组
        .btn-group {
          margin-bottom: 0;
          width: 100%; // 确保按钮组占满宽度

          // 登录按钮
          .login-btn {
            height: 48px;
            font-size: 16px;
            width: 100%; // 登录按钮占满宽度
            margin-bottom: 15px; // 与注册按钮拉开间距
          }

          // 注册按钮（单独一行）
          .register-btn {
            display: block; // 强制独占一行
            width: 100%; // 可选：注册按钮也占满宽度（也可只居中）
            text-align: center;
            font-size: 14px;
            color: #409eff;
            padding: 8px 0; // 增加点击区域

            &:hover {
              color: #66b1ff;
            }
          }
        }
      }
    }
  }
  :deep(.el-input__wrapper) {
    border-radius: 8px;
    transition: all 0.3s ease;
    &:focus-within {
      border-color: #409eff;
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
    }
  }

  // 响应式适配：小屏幕下改为上下布局
  @media (max-width: 992px) {
    flex-direction: column;

    .login-bg-section {
      width: 100%;
      height: 40%; // 移动端背景区占40%高度
    }

    .login-form-section {
      width: 100%;
      height: 60%; // 移动端登录区占60%高度
    }
  }

  // 超小屏适配
  @media (max-width: 576px) {
    .login-form-wrapper {
      padding: 30px 20px !important;
    }
  }
}
</style>